import type {CreateExtArgs, DalItem, DalValue, DmApis, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {
    BuilderPresets,
    BuilderStyle,
    BuilderTypes,
    CompRef,
    InnerElementPath,
    InnerElementsMap,
    NestedDataPathSegment,
    Pointer,
    PossibleStyleForBuilderComponent,
    SerializedCompStructure,
    SlotDataType,
    SlotsDataItem
} from '@wix/document-services-types'
import type {VariantsExtensionAPI} from '../variants/variants'
import {stripHashIfExists} from '../../utils/refArrayUtils'
import type {SchemaExtensionAPI} from '../schema/schema'
import {ReportableError} from '@wix/document-manager-utils'
import {COMP_DATA_QUERY_KEYS_WITH_STYLE, DATA_TYPES} from '../../constants/constants'
import isNil from 'lodash/isNil'
import _ from 'lodash'
import type {DataExtensionAPI} from '../data'
import {validateLanguageCode} from '../../utils/builderUtils'
import {EVENTS, type SlotsExtensionAPI} from '../slots'
import type {ComponentsExtensionAPI} from '../components/components'
import {normalizeCompRefVariants} from '../../utils/variantsUtils'
import type {CssStyleExtensionAPI} from '../cssStyle/cssStyle'
import {removeDataByNamespace} from '../../utils/dataUtils'
import type {StylesExtensionApi} from '../style/style'

import {DATA_MODEL_HOOKS} from '../dataModel/hooks'
import type {HooksExtensionApi} from '../hooks/hooks'
import type {ManifestExtensionAPI} from './manifest'

interface InnerElementsApi extends ExtensionAPI {
    getInnerElement(compRef: CompRef, innerElementPath: InnerElementPath): DalItem | undefined
    getOrCreateInnerElement(compRef: CompRef, innerElementPath: InnerElementPath, pageId: string): DalItem
    getInnerElementData(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode?: string
    ): DalItem | undefined
    getInnerElementTranslationOverrides(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode: string
    ): DalItem | undefined
    setInnerElementData(compRef: CompRef, innerElementPath: InnerElementPath, data: any, languageCode?: string): void
    removeInnerElementTranslationOverrides(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode: string
    ): void
    removeInnerElementTranslationOverridesForAField(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode: string,
        field: string
    ): void
    getInnerElementStyle(compRef: CompRef, innerElementPath: InnerElementPath): PossibleStyleForBuilderComponent
    setInnerElementStyle(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        style: Partial<BuilderStyle>,
        shouldRemoveRelatedProperties?: boolean
    ): void
    connectToGlobalStyle(
        componentPointer: CompRef,
        innerElementPath: InnerElementPath,
        globalStylePointerOrId: Pointer | string
    ): void
    removeInnerElementStyleProperties(compRef: CompRef, innerElementPath: InnerElementPath, paths: string[]): void
    getInnerElementPresets(compRef: CompRef, innerElementPath: InnerElementPath): BuilderPresets | undefined
    setInnerElementPresets(compRef: CompRef, innerElementPath: InnerElementPath, presets: BuilderPresets): void
    serializeInnerElementPath(innerElementPath: InnerElementPath): string
    setInnerElementSlots(
        componentToAddPointer: CompRef,
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        slotName: string,
        componentDefinition: SerializedCompStructure,
        nestedDataPath?: NestedDataPathSegment[]
    ): Pointer
    removeInnerElementSlot(compRef: CompRef, innerElementPath: InnerElementPath, slotName: string): void
    getInnerElementSlotItem(compRef: CompRef, innerElementPath: InnerElementPath): DalValue | void
    getInnerElementSlots(compRef: CompRef, innerElementPath: InnerElementPath): Record<string, CompRef> | void
    removeInnerElementDataProperties(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        propsToRemove: string[]
    ): void
    getInnerElementSlotsName(compRef: CompRef, innerElementPath: InnerElementPath): string[]
    removeInnerElement(compRef: CompRef, innerElementPath: InnerElementPath): void
    scoped: {
        removeStyle(compRef: CompRef, innerElementPath: InnerElementPath): void
    }
    bindings: {
        bind(
            componentPointer: CompRef,
            innerElementPath: InnerElementPath,
            contextType: string,
            bindingsMap: Record<string, string>
        ): void
        bindAll(
            componentPointer: CompRef,
            innerElementPath: InnerElementPath,
            contextType: string,
            bindingsMap: Record<string, string>
        ): void
        unbindProperties(
            componentPointer: CompRef,
            innerElementPath: InnerElementPath,
            contextType: string,
            properties: string[]
        ): void
        getBoundProperties(
            componentPointer: CompRef,
            innerElementPath: InnerElementPath
        ): Record<string, Record<string, string>>
    }
}

interface InnerElementsExtensionAPI extends ExtensionAPI {
    innerElements: InnerElementsApi
}

function throwError(message: string, extras: any = {}) {
    throw new ReportableError({errorType: 'InnerElementsError', message, ...extras})
}

const createExtension = (): Extension => {
    const createExtensionAPI = (args: CreateExtArgs): InnerElementsExtensionAPI => {
        const {dal, pointers, eventEmitter, coreConfig} = args
        const {experimentInstance} = coreConfig
        const {
            schemaAPI,
            dataModel,
            variants,
            data: dataExtension,
            slots,
            components,
            cssStyle,
            style,
            hooks
        } = args.extensionAPI as SchemaExtensionAPI &
            DataModelExtensionAPI &
            DataExtensionAPI &
            VariantsExtensionAPI &
            SlotsExtensionAPI &
            ComponentsExtensionAPI &
            CssStyleExtensionAPI &
            StylesExtensionApi &
            HooksExtensionApi

        const namespace = DATA_TYPES.innerElements

        function innerElementsPointer(id: string, overrides?: Partial<Pointer>): Pointer {
            return pointers.getPointer(id, DATA_TYPES.innerElements, overrides)
        }

        function getComp(compRef: CompRef): DalItem {
            const comp = dal.get(compRef)
            if (!comp) {
                throwError('Component not found', {compRef})
            }
            return comp
        }

        function getOrCreateInnerElementsMapPointerFromComp(compRef: CompRef): Pointer {
            const comp = getComp(compRef)
            let innerElementsQuery = stripHashIfExists(comp.innerElementsQuery)
            if (!innerElementsQuery) {
                const mapType = builderTypes(comp.componentType).innerElementsMap
                const mapPointer = dataModel.components.addItem(compRef, namespace, {
                    type: mapType,
                    metaData: {
                        pageId: compRef.pageId
                    }
                })
                innerElementsQuery = mapPointer.id
            }
            return innerElementsPointer(innerElementsQuery)
        }

        function getOrCreateInnerElementsMapPointerFromInnerElement(innerElementId: string): Pointer {
            const pointer = innerElementsPointer(innerElementId)
            const innerElement = dal.get(pointer)
            if (!innerElement) {
                throwError('Inner element not found', {innerElementId})
            }
            if (!innerElement.innerElementsQuery) {
                const mapPointer = dataModel.addItem(
                    {
                        type: builderTypes(innerElement.referredType).innerElementsMap
                    },
                    namespace,
                    innerElement.metaData.pageId!
                )
                dataModel.linkDataToItemByType(
                    pointer,
                    mapPointer.id,
                    DATA_TYPES.innerElements,
                    COMP_DATA_QUERY_KEYS_WITH_STYLE.innerElements
                )
            }
            const updatedElement = dal.get(pointer)
            return innerElementsPointer(stripHashIfExists(updatedElement.innerElementsQuery!))
        }

        function getOrCreateInnerElementFromComponent(
            compRef: CompRef,
            innerElementName: string,
            pageId: string
        ): DalItem {
            const comp = getComp(compRef)
            const compType = comp.componentType
            const innerType = getReferredTypeForNestedElement(compType, innerElementName)

            const mapPointer = getOrCreateInnerElementsMapPointerFromComp(compRef)
            const map = dal.get(mapPointer)
            if (!map[DATA_TYPES.innerElements]?.[innerElementName]) {
                const ptr = dataModel.addItem(
                    {
                        type: 'InnerElement',
                        referredType: innerType
                    },
                    namespace,
                    pageId
                )

                const clonedMap = _.cloneDeep(map)
                if (!clonedMap[DATA_TYPES.innerElements]) {
                    clonedMap[DATA_TYPES.innerElements] = {}
                }
                clonedMap[DATA_TYPES.innerElements][innerElementName] = ptr.id
                dataModel.addDeserializedItem(clonedMap, DATA_TYPES.innerElements, pageId, clonedMap.id)
            }

            // Possible micro-optimization: No need for dal accesses here, we already have the info in scope
            const updatedMap = dal.get(mapPointer)
            const innerElementId = stripHashIfExists(updatedMap[DATA_TYPES.innerElements][innerElementName])
            return dal.get(innerElementsPointer(innerElementId))
        }

        function getOrCreateInnerElementFromInnerElement(pageId: string, source: DalItem, targetName: string): DalItem {
            const mapPointer = getOrCreateInnerElementsMapPointerFromInnerElement(source.id!)
            const map = dal.get(mapPointer)
            const targetReferredType = getReferredTypeForNestedElement(source.referredType, targetName)
            if (!map[DATA_TYPES.innerElements]?.[targetName]) {
                const ptr = dataModel.addItem(
                    {
                        type: 'InnerElement',
                        referredType: targetReferredType
                    },
                    namespace,
                    pageId
                )
                const clonedMap = _.cloneDeep(map)
                if (!clonedMap[DATA_TYPES.innerElements]) {
                    clonedMap[DATA_TYPES.innerElements] = {}
                }
                clonedMap[DATA_TYPES.innerElements][targetName] = ptr.id
                dataModel.addDeserializedItem(clonedMap, DATA_TYPES.innerElements, pageId, clonedMap.id)
            }
            const updatedMap = dal.get(mapPointer)
            return dal.get(innerElementsPointer(stripHashIfExists(updatedMap[DATA_TYPES.innerElements][targetName])))
        }

        function getOrCreateInnerElement(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            pageId: string
        ): DalItem {
            const [firstName, ...restOfNames] = innerElementPath
            const firstElement = getOrCreateInnerElementFromComponent(compRef, firstName, pageId)
            return restOfNames.reduce(
                (acc, name) => getOrCreateInnerElementFromInnerElement(pageId, acc, name),
                firstElement
            )
        }

        function getInnerElementFromComponent(compRef: CompRef, innerElementName: string): DalItem | undefined {
            const comp = getComp(compRef)
            const innerElementsQuery = stripHashIfExists(comp.innerElementsQuery)
            if (!innerElementsQuery) {
                return undefined
            }
            const map = dal.get(innerElementsPointer(innerElementsQuery))
            if (!map) {
                return undefined
            }
            const innerElementId = stripHashIfExists(map[DATA_TYPES.innerElements][innerElementName])
            if (!innerElementId) {
                return undefined
            }
            return dal.get(innerElementsPointer(innerElementId))
        }

        function getInnerElementFromInnerElement(source: DalItem, innerElementName: string): DalItem | undefined {
            const innerElementsQuery = stripHashIfExists(source.innerElementsQuery)
            if (!innerElementsQuery) {
                return undefined
            }
            const map = dal.get(innerElementsPointer(innerElementsQuery))
            if (!map) {
                return undefined
            }
            const innerElementId = stripHashIfExists(map[DATA_TYPES.innerElements][innerElementName])
            if (!innerElementId) {
                return undefined
            }
            return dal.get(innerElementsPointer(innerElementId))
        }

        function getInnerElement(compRef: CompRef, innerElementPath: InnerElementPath): DalItem | undefined {
            const [firstName, ...restOfNames] = innerElementPath
            const firstElement = getInnerElementFromComponent(compRef, firstName)
            return restOfNames.reduce(
                (acc, name) => (acc ? getInnerElementFromInnerElement(acc, name) : undefined),
                firstElement
            )
        }

        function builderTypes(compType: string): BuilderTypes {
            return (
                schemaAPI.getDefinition(compType)?.builderTypes ??
                throwError(`No builderTypes for component type ${compType}`, {compType})
            )
        }

        function getReferredTypeForNestedElement(sourceType: string, innerElementName: string): string {
            const sourceBuilderTypes = builderTypes(sourceType)
            return (
                sourceBuilderTypes.innerElementTypes[innerElementName] ??
                throwError('Failed to get type info for innerElementType', {innerElementType: sourceType})
            )
        }

        function getInnerElementData(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode?: string
        ): DalItem | undefined {
            const innerElement = getInnerElement(compRef, innerElementPath)
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const dataId = stripHashIfExists(innerElement?.dataQuery)
            return dataExtension.getInLanguage(pageId, dataId, languageCode)
        }

        function getInnerElementTranslationOverrides(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode: string
        ): DalItem | undefined {
            validateLanguageCode(languageCode)

            const innerElement = getInnerElement(compRef, innerElementPath)
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const dataId = stripHashIfExists(innerElement?.dataQuery)

            return dataModel.getItem(dataId, DATA_TYPES.data, pageId, languageCode)
        }

        function setInnerElementData(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            dataItem: any,
            languageCode?: string
        ): void {
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const innerElement = getOrCreateInnerElement(compRef, innerElementPath, pageId)

            const expectedDataType = getExpectedDataType(compRef, innerElementPath)
            const dataId = stripHashIfExists(innerElement.dataQuery)

            hooks.executeHook(
                DATA_MODEL_HOOKS.DATA.INNER_ELEMENT_UPDATE_BEFORE.createEvent({
                    componentPointer: compRef,
                    dataItem
                })
            )

            dataExtension.updateInLanguage(
                innerElementsPointer(innerElement.id!),
                dataItem,
                pageId,
                dataId,
                languageCode,
                {refTypes: [expectedDataType]}
            )
        }

        function setInnerElementSlots(
            componentToAddPointer: CompRef,
            slottedComponent: CompRef,
            innerElementPath: InnerElementPath,
            slotName: string,
            componentDefinition: SerializedCompStructure,
            nestedDataPath?: NestedDataPathSegment[]
        ) {
            const ownerOfComponentInSlotPointer = slots.getOwnerOfComponentInSlot(slottedComponent)
            const addedComponentPointer = components.addComponentV2(
                componentToAddPointer,
                slottedComponent,
                componentDefinition,
                {isPlugin: true}
            )
            const pageId = pointers.structure.getPageOfComponent(slottedComponent).id
            const innerElement = getOrCreateInnerElement(slottedComponent, innerElementPath, pageId)
            const slotsDataType = getExpectedSlotsType(slottedComponent, innerElementPath)

            if (experimentInstance.isOpen('dm_refactorStaticSlotToDynamic')) {
                const slotGuid = slots.updateSlotInData(
                    slottedComponent,
                    slotName,
                    nestedDataPath ?? [],
                    innerElementPath
                )
                slots.updateSlot(
                    innerElementsPointer(innerElement.id!) as CompRef,
                    slotGuid,
                    componentToAddPointer,
                    slotsDataType
                )
            } else {
                slots.updateSlot(
                    innerElementsPointer(innerElement.id!) as CompRef,
                    slotName,
                    componentToAddPointer,
                    slotsDataType
                )
            }

            eventEmitter.emit(
                EVENTS.SLOTS.AFTER_POPULATE,
                slottedComponent,
                ownerOfComponentInSlotPointer,
                addedComponentPointer
            )

            return addedComponentPointer
        }

        function removeInnerElementSlot(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            slotKeyName: string
        ): void {
            _throwIfNotOfTypeSlot(compRef, innerElementPath, slotKeyName)

            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                return
            }

            const innerElementPointer = innerElementsPointer(innerElement.id!)
            const slotsData = slots.getSlotsDataItem(innerElementPointer as CompRef)
            if (!slotsData?.slots?.[slotKeyName]) {
                return
            }

            const slottedComponentId = slotsData.slots[slotKeyName]
            _removeSlottedComponentFromDalAndParent(compRef, slottedComponentId)
            _cleanSlotDataAndInnerElementMap(compRef, innerElementPointer, innerElementPath, slotsData, slotKeyName)
        }

        function _removeSlottedComponentFromDalAndParent(compRef: CompRef, slottedComponentId: string) {
            const slottedComponentPointer = pointers.getPointer(slottedComponentId, compRef.type) as CompRef
            if (dal.has(slottedComponentPointer)) {
                components.removeComponent(slottedComponentPointer)
            }
        }

        function _cleanSlotDataAndInnerElementMap(
            compRef: CompRef,
            innerElementPointer: any,
            innerElementPath: string[],
            slotsData: any,
            slotName: string
        ) {
            delete slotsData.slots[slotName]
            if (_.isEmpty(slotsData.slots)) {
                dataModel.components.removeItem(innerElementPointer, DATA_TYPES.slots)
                const innerElement = dal.get(innerElementPointer)
                const hasChildInnerElements =
                    innerElement?.innerElementsQuery &&
                    !_.isEmpty(
                        dal.get(innerElementsPointer(stripHashIfExists(innerElement.innerElementsQuery)))?.[
                            DATA_TYPES.innerElements
                        ]
                    )
                if (!hasChildInnerElements) {
                    _removeInnerElementFromParentMap(compRef, innerElementPath)
                }
            } else {
                dataModel.components.addItem(innerElementPointer, DATA_TYPES.slots, slotsData)
            }
        }

        const _isContainerStatic = (slotDefinition: any) => _.has(slotDefinition, ['metaData', 'containerType'])

        const _isContainerDynamic = (slotDefinition: any) =>
            _.has(slotDefinition, ['metaData', 'data', 'items', 'content', 'container', 'containerType'])

        function _throwIfNotOfTypeSlot(compRef: CompRef, innerElementPath: InnerElementPath, slotKeyName: string) {
            const manifestAPI = args.extensionAPI.manifest as ManifestExtensionAPI
            const csmSlots = (manifestAPI as any).getData(compRef, true)
            _.forEach(csmSlots, csmSlot => {
                if (!_.isEqual(innerElementPath, csmSlot.path)) {
                    return
                }
                if (_isContainerStatic(csmSlot)) {
                    if (csmSlot.prop !== slotKeyName) {
                        return
                    }
                    const type = csmSlot.metaData?.containerType
                    if (type !== 'slot') {
                        throwError('The provided key is not of type slot')
                    }
                } else if (_isContainerDynamic(csmSlot)) {
                    if (!_.isArray(csmSlot.value)) {
                        return
                    }
                    if (!_.some(csmSlot.value, (item: any) => item.id === slotKeyName)) {
                        return
                    }
                    const type = _.get(csmSlot, ['metaData', 'data', 'items', 'content', 'container', 'containerType'])
                    if (type !== 'slot') {
                        throwError('The provided key is not of type slot')
                    }
                }
            })
        }

        function _getChildInnerElementsRecursively(
            innerElement: DalItem,
            basePath: InnerElementPath
        ): InnerElementPath[] {
            const innerElementsQuery = stripHashIfExists(innerElement.innerElementsQuery)
            if (!innerElementsQuery) {
                return []
            }
            const map = dal.get(innerElementsPointer(innerElementsQuery))
            if (!map?.[DATA_TYPES.innerElements]) {
                return []
            }

            const children = Object.entries(map[DATA_TYPES.innerElements]) as [string, string][]

            return children.reduce((acc, [name, id]) => {
                const childPath = [...basePath, name]
                const childInnerElement = dal.get(innerElementsPointer(stripHashIfExists(id)))
                if (childInnerElement) {
                    acc.push(..._getChildInnerElementsRecursively(childInnerElement, childPath))
                }
                acc.push(childPath)
                return acc
            }, [] as InnerElementPath[])
        }

        function _removeInnerElementFromParentMap(compRef: CompRef, innerElementPath: InnerElementPath): void {
            const names = innerElementPath
            const elementName = names[names.length - 1]
            let mapPointer: Pointer
            let parentPointer: Pointer

            if (names.length === 1) {
                const comp = getComp(compRef)
                const innerElementsQuery = stripHashIfExists(comp.innerElementsQuery)
                if (!innerElementsQuery) {
                    return
                }
                mapPointer = innerElementsPointer(innerElementsQuery)
                parentPointer = compRef
            } else {
                const parentPath = innerElementPath.slice(0, -1)
                const parentInnerElement = getInnerElement(compRef, parentPath)
                if (!parentInnerElement) {
                    return
                }
                mapPointer = getOrCreateInnerElementsMapPointerFromInnerElement(parentInnerElement.id!)
                parentPointer = innerElementsPointer(parentInnerElement.id!)
            }

            const innerMapPointer = pointers.getInnerPointer(mapPointer, [DATA_TYPES.innerElements, elementName])
            dal.remove(innerMapPointer)

            if (_.isEmpty(dal.get(mapPointer)?.[DATA_TYPES.innerElements])) {
                dal.set(parentPointer, _.omit(dal.get(parentPointer), [COMP_DATA_QUERY_KEYS_WITH_STYLE.innerElements]))
                dataModel.removeItemRecursively(mapPointer)
            }
        }

        function getInnerElementSlotItem(compRef: CompRef, innerElementPath: InnerElementPath): SlotsDataItem | void {
            const comp = dal.get(compRef)
            if (!comp) {
                throwError('Component not found', {compRef})
            }

            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                return
            }

            const innerElementPointer = innerElementsPointer(innerElement!.id!)
            return slots.getSlotsDataItem(innerElementPointer as CompRef)
        }

        function getInnerElementSlots(
            compRef: CompRef,
            innerElementPath: InnerElementPath
        ): Record<string, CompRef> | void {
            const comp = dal.get(compRef)
            if (!comp) {
                throwError('Component not found', {compRef})
            }

            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                return
            }

            const innerElementPointer = innerElementsPointer(innerElement!.id!)
            return slots.getSlotsData(innerElementPointer as CompRef, 'DESKTOP')
        }

        function getInnerElementSlotsName(compRef: CompRef, innerElementPath: InnerElementPath): string[] {
            return _.keys(getInnerElementSlots(compRef, innerElementPath))
        }

        function removeInnerElement(compRef: CompRef, innerElementPath: InnerElementPath): void {
            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                return
            }

            const allInnerElementsToRemove = [
                ..._getChildInnerElementsRecursively(innerElement, innerElementPath),
                innerElementPath
            ]

            for (const path of allInnerElementsToRemove) {
                const slotNames = getInnerElementSlotsName(compRef, path)
                for (const slotName of slotNames) {
                    removeInnerElementSlot(compRef, path, slotName)
                }
            }

            _removeInnerElementFromParentMap(compRef, innerElementPath)
            dataModel.removeItemRecursively(innerElementsPointer(innerElement.id!))
        }

        function removeInnerElementTranslationOverrides(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode: string
        ): void {
            validateLanguageCode(languageCode)

            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const innerElement = getInnerElement(compRef, innerElementPath)
            const dataId = stripHashIfExists(innerElement?.dataQuery)
            const translationOverridesPointer = pointers.multilingualTranslations.translationDataItem(
                pageId,
                languageCode,
                dataId
            )
            dataModel.removeItemRecursively(translationOverridesPointer)
        }

        function removeInnerElementTranslationOverridesForAField(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode: string,
            field: string
        ) {
            validateLanguageCode(languageCode)

            const translationOverridesDataItem = getInnerElementTranslationOverrides(
                compRef,
                innerElementPath,
                languageCode
            )
            if (translationOverridesDataItem) {
                delete translationOverridesDataItem[field]
                if (_.isEmpty(_.omit(translationOverridesDataItem, ['_id']))) {
                    removeInnerElementTranslationOverrides(compRef, innerElementPath, languageCode)
                } else {
                    setInnerElementData(compRef, innerElementPath, translationOverridesDataItem, languageCode)
                }
            }
        }

        function recurseBuilderTypes(compRef: CompRef, innerElementPath: InnerElementPath): BuilderTypes {
            const comp = getComp(compRef)
            const compBuilderTypes = builderTypes(comp.componentType)
            if (innerElementPath.length < 1) {
                return compBuilderTypes
            }
            const names = innerElementPath
            return names.reduce((acc, name) => {
                const childType = acc.innerElementTypes[name]
                return builderTypes(childType)
            }, compBuilderTypes)
        }

        function getExpectedDataType(compRef: CompRef, innerElementPath: InnerElementPath): string {
            return recurseBuilderTypes(compRef, innerElementPath).data
        }

        function getExpectedStyleType(compRef: CompRef, innerElementPath: InnerElementPath): BuilderTypes['style'] {
            return recurseBuilderTypes(compRef, innerElementPath).style
        }

        function getExpectedPresetsType(compRef: CompRef, innerElementPath: InnerElementPath): string {
            return recurseBuilderTypes(compRef, innerElementPath).presets
        }

        function getExpectedSlotsType(compRef: CompRef, innerElementPath: InnerElementPath): SlotDataType {
            return recurseBuilderTypes(compRef, innerElementPath).slots as SlotDataType
        }

        function autoType<T extends Record<string, any>>(obj: T, expectedType: string): T {
            if (isNil(obj)) {
                return obj
            }
            const typeWasExplicitlyDeclared = obj.type !== undefined
            if (typeWasExplicitlyDeclared && obj.type !== expectedType) {
                throwError('Type mismatch', {expectedType, actualType: obj.type})
            }
            return typeWasExplicitlyDeclared ? obj : {...obj, type: expectedType}
        }

        const getEmptyInnerElementStyle = (compRef: CompRef, innerElementPath: InnerElementPath): BuilderStyle => {
            const styleType = getExpectedStyleType(compRef, innerElementPath)
            return {
                type: styleType
            }
        }

        const getInnerElementStyle = (
            compRef: CompRef,
            innerElementPath: InnerElementPath
        ): PossibleStyleForBuilderComponent => {
            const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)

            if (!dal.has(_.omit(compRef, ['variants']))) {
                throwError('Component not found', {compRef: compRefWithNormalizedVariants})
            }

            const innerElement = getInnerElement(compRefWithNormalizedVariants, innerElementPath)
            if (!innerElement) {
                return
            }
            return cssStyle.components.getCSSStyleByPointer(
                innerElementsPointer(innerElement.id!, {
                    variants: compRefWithNormalizedVariants.variants
                })
            )
        }

        const getResolvedInnerElementStyle = (
            compRef: CompRef,
            innerElementPath: InnerElementPath
        ): BuilderStyle | undefined => {
            let styleItem = getInnerElementStyle(compRef, innerElementPath)

            if (styleItem?.type === 'GlobalStyle') {
                const globalStylePointer = style.global.getPointer(styleItem.id)
                styleItem = style.global.get(globalStylePointer) as BuilderStyle
            }

            return styleItem
        }

        function removeInnerElementDataProperties(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            propsToRemove: string[]
        ): void {
            const innerElement = getInnerElement(compRef, innerElementPath)
            const innerElementPointer = innerElementsPointer(
                innerElement!.id!,
                compRef.variants ? {variants: compRef.variants} : {}
            )

            removeDataByNamespace(args.extensionAPI, innerElementPointer, DATA_TYPES.data, propsToRemove, true)
        }

        const connectToGlobalStyle = (
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            globalStylePointerOrId: Pointer | string
        ): void => {
            const globalStyleDefinition = style.global.definition.get(globalStylePointerOrId)
            const expectedStyleType = getExpectedStyleType(compRef, innerElementPath)
            if (globalStyleDefinition.styleType !== expectedStyleType) {
                throw new Error(
                    `The style type ${globalStyleDefinition.styleType} is not derived from the component manifest. The inner element style type is ${expectedStyleType}`
                )
            }

            const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)
            const pageId = pointers.structure.getPageOfComponent(compRefWithNormalizedVariants).id
            const innerElement = getOrCreateInnerElement(compRefWithNormalizedVariants, innerElementPath, pageId)
            const innerElementPointer = innerElementsPointer(innerElement.id!, {
                variants: compRefWithNormalizedVariants.variants
            })

            style.connectToGlobalStyle(innerElementPointer, globalStylePointerOrId)
        }

        const getBindingsListFromInnerElement = (innerElementPointer: Pointer): DalItem | undefined => {
            return dataModel.components.getItem(innerElementPointer as CompRef, DATA_TYPES.bindings)
        }

        const bind = (
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            contextType: string,
            bindingsMap: Record<string, string>,
            replace: boolean
        ): void => {
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const innerElement = getOrCreateInnerElement(compRef, innerElementPath, pageId)
            const innerElementPointer = innerElementsPointer(innerElement.id!)

            const existingBindingsList = getBindingsListFromInnerElement(innerElementPointer)

            if (!existingBindingsList) {
                dataModel.components.addItem(innerElementPointer, DATA_TYPES.bindings, {
                    type: 'BindingsList',
                    values: [
                        {
                            type: 'ContextBindingConfig',
                            contextProvider: contextType,
                            properties: bindingsMap
                        }
                    ]
                })
                return
            }

            const bindingConfigs = existingBindingsList?.values ?? []

            const configAlreadyDefined = _.find(
                bindingConfigs,
                (config: DalItem) => config.contextProvider === contextType
            )

            if (!configAlreadyDefined) {
                dataModel.components.addItem(innerElementPointer, DATA_TYPES.bindings, {
                    ...existingBindingsList,
                    values: [
                        ...bindingConfigs,
                        {
                            type: 'ContextBindingConfig',
                            contextProvider: contextType,
                            properties: bindingsMap
                        }
                    ]
                })
                return
            }

            const updatedConfig = {
                ...configAlreadyDefined,
                properties: replace ? bindingsMap : {...configAlreadyDefined.properties, ...bindingsMap}
            }

            const updatedBindingConfigs = bindingConfigs.map((config: DalItem) =>
                config.id === updatedConfig.id ? updatedConfig : config
            )

            dataModel.components.addItem(innerElementPointer, DATA_TYPES.bindings, {
                ...existingBindingsList,
                values: updatedBindingConfigs
            })
        }

        const unbindProperties = (
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            contextType: string,
            properties: string[]
        ): void => {
            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                throwError('Inner element not found', {compRef, innerElementPath})
            }

            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const innerElementPointer = innerElementsPointer(innerElement!.id!)

            const existingBindingsList = getBindingsListFromInnerElement(innerElementPointer)

            if (!existingBindingsList) {
                throwError('This inner element does not have bindings', {compRef, innerElementPath})
            }

            const existingContextBindingConfigs = existingBindingsList?.values ?? []

            const contextConfigAlreadyDefined = _.find(
                existingContextBindingConfigs,
                (config: DalItem) => config.contextProvider === contextType
            )

            if (!contextConfigAlreadyDefined) {
                throwError(`Context not found: ${contextType}`, {compRef, innerElementPath, contextType})
            }

            const newProperties = _.omit(contextConfigAlreadyDefined!.properties, properties)
            const updatedConfig = {
                ...contextConfigAlreadyDefined,
                properties: newProperties
            }
            dataModel.addItem(updatedConfig, DATA_TYPES.bindings, pageId, contextConfigAlreadyDefined!.id)
        }

        const getBoundProperties = (
            compRef: CompRef,
            innerElementPath: InnerElementPath
        ): Record<string, Record<string, string>> => {
            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                return {}
            }

            const innerElementPointer = innerElementsPointer(innerElement.id!)
            const existingBindingsList = getBindingsListFromInnerElement(innerElementPointer)

            if (!existingBindingsList?.values || existingBindingsList.values.length === 0) {
                return {}
            }

            const bindings: Record<string, Record<string, string>> = {}

            for (const config of existingBindingsList.values) {
                bindings[(config as DalItem).contextProvider] = (config as DalItem).properties
            }

            return bindings
        }

        return {
            innerElements: {
                getInnerElement,
                getOrCreateInnerElement,
                serializeInnerElementPath(innerElementPath: InnerElementPath) {
                    return innerElementPath.join('|')
                },
                getInnerElementData,
                getInnerElementTranslationOverrides,
                setInnerElementData,
                removeInnerElementTranslationOverrides,
                removeInnerElementTranslationOverridesForAField,
                getInnerElementStyle,
                setInnerElementStyle(
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    partialStyle: Partial<BuilderStyle>,
                    shouldRemoveRelatedProperties = true
                ) {
                    const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)
                    const pageId = pointers.structure.getPageOfComponent(compRefWithNormalizedVariants).id
                    const innerElement = getOrCreateInnerElement(
                        compRefWithNormalizedVariants,
                        innerElementPath,
                        pageId
                    )
                    const existingStyle =
                        getResolvedInnerElementStyle(compRefWithNormalizedVariants, innerElementPath) ??
                        getEmptyInnerElementStyle(compRefWithNormalizedVariants, innerElementPath)

                    const valueToUpdate = cssStyle.getUpdatedCSSStyleItemForUpdate(
                        existingStyle,
                        partialStyle,
                        shouldRemoveRelatedProperties
                    )
                    variants.updateDataConsideringVariants({
                        pointerWithVariants: innerElementsPointer(innerElement.id!, {
                            variants: compRefWithNormalizedVariants.variants
                        }),
                        valueToUpdate,
                        namespace: DATA_TYPES.theme,
                        referenceName: COMP_DATA_QUERY_KEYS_WITH_STYLE.style
                    })
                },
                connectToGlobalStyle,
                removeInnerElementStyleProperties(
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    paths: string[]
                ) {
                    const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)
                    const innerElement = getInnerElement(compRefWithNormalizedVariants, innerElementPath)
                    if (!innerElement?.styleId) {
                        return
                    }

                    const styleItem = getResolvedInnerElementStyle(compRefWithNormalizedVariants, innerElementPath)
                    if (styleItem) {
                        const valueToUpdate = cssStyle.getUpdatedCSSStyleItemForRemove(styleItem, paths)
                        if (valueToUpdate) {
                            variants.updateDataConsideringVariants({
                                pointerWithVariants: innerElementsPointer(innerElement.id!, {
                                    variants: compRefWithNormalizedVariants.variants
                                }),
                                valueToUpdate,
                                namespace: DATA_TYPES.theme,
                                referenceName: COMP_DATA_QUERY_KEYS_WITH_STYLE.style
                            })
                        }
                    }
                },
                scoped: {
                    removeStyle(compRef: CompRef, innerElementPath: InnerElementPath) {
                        const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)
                        const innerElement = getInnerElement(compRefWithNormalizedVariants, innerElementPath)
                        if (!innerElement?.styleId) {
                            return
                        }
                        variants.removeComponentDataConsideringVariants(
                            innerElementsPointer(innerElement.id!, {variants: compRefWithNormalizedVariants.variants}),
                            DATA_TYPES.theme
                        )
                    }
                },
                getInnerElementPresets(compRef: CompRef, innerElementPath: InnerElementPath) {
                    const comp = dal.get(compRef)
                    if (!comp) {
                        throwError('Component not found', {compRef})
                    }

                    const innerElement = getInnerElement(compRef, innerElementPath)
                    if (!innerElement?.presetsQuery) {
                        return undefined
                    }

                    return variants.getComponentItemConsideringVariants(
                        innerElementsPointer(innerElement.id!, {variants: compRef.variants}),
                        DATA_TYPES.presets
                    )
                },
                setInnerElementPresets(
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    presets: BuilderPresets
                ): void {
                    const pageId = pointers.structure.getPageOfComponent(compRef).id
                    const innerElement = getOrCreateInnerElement(compRef, innerElementPath, pageId)
                    const expectedPresetsType = getExpectedPresetsType(compRef, innerElementPath)
                    variants.updateDataConsideringVariants({
                        pointerWithVariants: innerElementsPointer(innerElement.id!, {
                            variants: compRef.variants
                        }),
                        valueToUpdate: autoType(presets, expectedPresetsType),
                        namespace: DATA_TYPES.presets,
                        referenceName: COMP_DATA_QUERY_KEYS_WITH_STYLE.presets
                    })
                },
                setInnerElementSlots,
                removeInnerElementSlot,
                getInnerElementSlots,
                getInnerElementSlotItem,
                getInnerElementSlotsName,
                removeInnerElementDataProperties,
                removeInnerElement,
                bindings: {
                    bind: (
                        compRef: CompRef,
                        innerElementPath: InnerElementPath,
                        contextType: string,
                        bindingsMap: Record<string, string>
                    ) => bind(compRef, innerElementPath, contextType, bindingsMap, false),
                    bindAll: (
                        compRef: CompRef,
                        innerElementPath: InnerElementPath,
                        contextType: string,
                        bindingsMap: Record<string, string>
                    ) => bind(compRef, innerElementPath, contextType, bindingsMap, true),
                    unbindProperties,
                    getBoundProperties
                }
            }
        }
    }

    const createPointersMethods = ({dal, pointers}: DmApis) => {
        const getInnerElement = (id: string, pageId?: string) =>
            pointers.data.getItem(DATA_TYPES.innerElements, id, pageId)

        const getInnerElementsMap = (innerElementPointer: Pointer) => {
            const {innerElements} =
                (dal.get(
                    getInnerElement(
                        dal.getWithPath(innerElementPointer, [
                            COMP_DATA_QUERY_KEYS_WITH_STYLE[DATA_TYPES.innerElements]
                        ])
                    )
                ) as InnerElementsMap) ?? {}

            return Object.values(innerElements ?? []).map((innerElementId: string) =>
                getInnerElement(stripHashIfExists(innerElementId))
            )
        }

        const getInnerElements = (innerElementPointer: Pointer, results: Pointer[], shouldSkip = false): void => {
            const innerElements = getInnerElementsMap(innerElementPointer)

            for (const innerElement of innerElements) {
                getInnerElements(innerElement, results)
            }

            if (!shouldSkip) {
                results.push(innerElementPointer)
            }
        }

        const getComponentInnerElementsRecursively = (componentPointer: CompRef): Pointer[] => {
            const results: Pointer[] = []
            getInnerElements(componentPointer, results, true)
            return results
        }

        return {
            innerElements: {
                getComponentInnerElementsRecursively
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {innerElements} = extensionAPI as InnerElementsExtensionAPI
        return {
            innerElements: {
                getInnerElementTranslationOverrides: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    languageCode: string
                ) => innerElements.getInnerElementTranslationOverrides(compRef, innerElementPath, languageCode),
                removeInnerElementTranslationOverrides: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    languageCode: string
                ) => innerElements.removeInnerElementTranslationOverrides(compRef, innerElementPath, languageCode),
                removeInnerElementTranslationOverridesForAField: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    languageCode: string,
                    field: string
                ) =>
                    innerElements.removeInnerElementTranslationOverridesForAField(
                        compRef,
                        innerElementPath,
                        languageCode,
                        field
                    ),
                removeInnerElementDataProperties: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    propsToRemove: string[]
                ) => innerElements.removeInnerElementDataProperties(compRef, innerElementPath, propsToRemove),
                serializeInnerElementPath: (innerElementPath: InnerElementPath) =>
                    innerElements.serializeInnerElementPath(innerElementPath),
                getInnerElementData: (compRef: CompRef, innerElementPath: InnerElementPath, languageCode?: string) =>
                    innerElements.getInnerElementData(compRef, innerElementPath, languageCode),
                setInnerElementData: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    data: any,
                    languageCode?: string
                ) => innerElements.setInnerElementData(compRef, innerElementPath, data, languageCode),
                getInnerElementStyle: (compRef: CompRef, innerElementPath: InnerElementPath) =>
                    innerElements.getInnerElementStyle(compRef, innerElementPath),
                setInnerElementStyle: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    style: Partial<BuilderStyle>,
                    shouldRemoveRelatedProperties?: boolean
                ) =>
                    innerElements.setInnerElementStyle(compRef, innerElementPath, style, shouldRemoveRelatedProperties),
                connectToGlobalStyle: (
                    componentPointer: CompRef,
                    innerElementPath: InnerElementPath,
                    globalStylePointerOrId: Pointer | string
                ) => innerElements.connectToGlobalStyle(componentPointer, innerElementPath, globalStylePointerOrId),
                removeInnerElementStyleProperties: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    paths: string[]
                ) => innerElements.removeInnerElementStyleProperties(compRef, innerElementPath, paths),
                getInnerElementPresets: (compRef: CompRef, innerElementPath: InnerElementPath) =>
                    innerElements.getInnerElementPresets(compRef, innerElementPath),
                setInnerElementPresets: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    presets: BuilderPresets
                ) => innerElements.setInnerElementPresets(compRef, innerElementPath, presets),
                setInnerElementSlots: (
                    componentToAddPointer: CompRef,
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    slotName: string,
                    componentDefinition: SerializedCompStructure,
                    nestedDataPath?: NestedDataPathSegment[]
                ) => {
                    const {manifest} = extensionAPI as ManifestExtensionAPI
                    manifest.validateContainerTypeForPopulation(compRef, slotName, innerElementPath)
                    return innerElements.setInnerElementSlots(
                        componentToAddPointer,
                        compRef,
                        innerElementPath,
                        slotName,
                        componentDefinition,
                        nestedDataPath
                    )
                },
                removeInnerElementSlot: (compRef: CompRef, innerElementPath: InnerElementPath, slotName: string) =>
                    innerElements.removeInnerElementSlot(compRef, innerElementPath, slotName),
                getInnerElementSlots: (compRef: CompRef, innerElementPath: InnerElementPath) =>
                    innerElements.getInnerElementSlots(compRef, innerElementPath),
                scoped: {
                    removeStyle: (compRef: CompRef, innerElementPath: InnerElementPath) =>
                        innerElements.scoped.removeStyle(compRef, innerElementPath)
                },
                removeInnerElement: (compRef: CompRef, innerElementPath: InnerElementPath) =>
                    innerElements.removeInnerElement(compRef, innerElementPath),
                bindings: {
                    bind: innerElements.bindings.bind,
                    bindAll: innerElements.bindings.bindAll,
                    unbindProperties: innerElements.bindings.unbindProperties,
                    getBoundProperties: innerElements.bindings.getBoundProperties
                }
            }
        }
    }

    return {
        name: 'innerElements',
        dependencies: new Set(['schema', 'data', 'dataModel', 'slots', 'variants', 'cssStyle']),
        createExtensionAPI,
        createPointersMethods,
        createPublicAPI
    }
}

export {createExtension}
export type {InnerElementsExtensionAPI, InnerElementsApi, InnerElementPath}
